import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import Icon from './Icon'
import { docNavigation } from '../../content/navigation'
import Cased from '../ui/Cased'

type DocLayoutProps = {
  eyebrow: string
  title: string
  intro: ReactNode
  children: ReactNode
  /** Content that spans the full width under the header. */
  wide?: boolean
  /** Optional element rendered beside the title, such as a specimen. */
  aside?: ReactNode
  /** For a page inside a section, the section it belongs to, shown as a breadcrumb. */
  section?: { label: string; to: string }
  /** The page's name in the breadcrumb, where its title is longer. */
  crumb?: string
}

/** The documentation's sections and pages, for the sidebar and the phone menu. */
/** A search field at the top of the menu; it opens the search page with the words. */
function DocSearch() {
  const navigate = useNavigate()
  const id = useId()
  return (
    <form
      className="doc-sidebar__search"
      role="search"
      onSubmit={(event) => {
        event.preventDefault()
        const value = String(new FormData(event.currentTarget).get('q') ?? '').trim()
        navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search')
      }}
    >
      <label className="mds-visually-hidden" htmlFor={id}>
        Search the documentation
      </label>
      <Icon name="search" size={16} />
      <input id={id} type="search" name="q" placeholder="Search" autoComplete="off" />
    </form>
  )
}

function DocNav() {
  return (
    <nav aria-label="Documentation">
      <DocSearch />
      {docNavigation.map((group) => (
        <div key={group.label} className="doc-sidebar__group">
          <p className="mds-eyebrow">{group.label}</p>
          <ul>
            {group.links.map((link) => (
              <li key={link.to}>
                <NavLink to={link.to} end className={({ isActive }) => `doc-sidebar__link${isActive ? ' active' : ''}`}>
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  )
}

/**
 * The sidebar scrolls on its own within the window. Fades at its edges show
 * when there is more above or below, and the current page's link starts in view.
 */
function DocSidebar() {
  const sidebar = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const [edges, setEdges] = useState({ above: false, below: false })

  const measure = useCallback(() => {
    const element = sidebar.current
    if (!element) return
    // As tall as the window allows from where the menu actually sits: lower at
    // the top of the page, under the banner and the page's own padding, and
    // higher once it is stuck under the header.
    element.style.maxHeight = `${Math.max(160, window.innerHeight - element.getBoundingClientRect().top - 16)}px`
    setEdges({
      above: element.scrollTop > 2,
      below: element.scrollTop + element.clientHeight < element.scrollHeight - 2,
    })
  }, [])

  useEffect(() => {
    const element = sidebar.current
    const active = element?.querySelector<HTMLElement>('.doc-sidebar__link.active')
    if (element && active) {
      const top = active.getBoundingClientRect().top - element.getBoundingClientRect().top + element.scrollTop
      if (top < element.scrollTop || top + active.offsetHeight > element.scrollTop + element.clientHeight)
        element.scrollTop = top - element.clientHeight / 3
    }
    measure()
    // When the content ends and the menu starts to ride up with it, the menu
    // shows its own end, so its last links are not left behind its fade.
    // It glides there once, as it releases, rather than snapping on every
    // scroll; back at the top of the page it glides back to where it started.
    const start = element?.scrollTop ?? 0
    let released = false
    let atTop = window.scrollY < 4
    // A slow, eased glide; the browser's own smooth scroll is too quick here.
    let frame = 0
    const glide = (target: number) => {
      if (!element) return
      window.cancelAnimationFrame(frame)
      const to = Math.max(0, Math.min(target, element.scrollHeight - element.clientHeight))
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        element.scrollTop = to
        return
      }
      const from = element.scrollTop
      const began = performance.now()
      const duration = 900
      const step = (now: number) => {
        const progress = Math.min(1, (now - began) / duration)
        // Ease out: quick to start, settling gently.
        element.scrollTop = from + (to - from) * (1 - Math.pow(1 - progress, 3))
        if (progress < 1) frame = window.requestAnimationFrame(step)
      }
      frame = window.requestAnimationFrame(step)
    }
    const follow = () => {
      const column = element?.parentElement
      if (element && column) {
        const stuckAt = parseFloat(getComputedStyle(column).top) || 0
        const riding = column.getBoundingClientRect().top < stuckAt - 1
        if (riding && !released) glide(element.scrollHeight)
        released = riding
        const top = window.scrollY < 4
        if (top && !atTop) glide(start)
        atTop = top
      }
      measure()
    }
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', follow, { passive: true })
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', follow)
    }
  }, [pathname, measure])

  return (
    <aside
      ref={sidebar}
      id="doc-sidebar"
      className={`doc-sidebar${edges.above ? ' has-above' : ''}${edges.below ? ' has-below' : ''}`}
      onScroll={measure}
    >
      <DocNav />
    </aside>
  )
}

const HIDDEN_KEY = 'mds:doc-menu'

/** Whether the visitor hid the desktop menu, remembered in this browser only. */
function readHidden() {
  try {
    return window.localStorage.getItem(HIDDEN_KEY) === 'hidden'
  } catch {
    return false
  }
}

export default function DocLayout({ eyebrow, title, intro, children, wide = false, aside, section, crumb }: DocLayoutProps) {
  const { pathname } = useLocation()
  const [hidden, setHidden] = useState(readHidden)
  const toggle = (next: boolean) => {
    setHidden(next)
    try {
      window.localStorage.setItem(HIDDEN_KEY, next ? 'hidden' : 'shown')
    } catch {
      // Without storage the choice lasts for this page.
    }
  }
  // Hidden, the control moves out to the window's left edge, inside the page margin.
  const column = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const element = column.current
    if (!element) return
    const place = () => {
      element.style.removeProperty('--doc-edge')
      if (hidden) element.style.setProperty('--doc-edge', `${4 - element.getBoundingClientRect().left}px`)
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [hidden])
  return (
    <div className={`mds-container doc-shell${hidden ? ' doc-shell--menu-hidden' : ''}`}>
      {/* The standard sidebar control sits at the top of the line between the
          menu and the page, and stays there when the menu is hidden. */}
      <div ref={column} className="doc-sidebar-column">
        <button
          type="button"
          className="mds-icon-button doc-menu-toggle"
          aria-label={hidden ? 'Show the documentation menu' : 'Hide the documentation menu'}
          aria-expanded={!hidden}
          aria-controls="doc-sidebar"
          title={hidden ? 'Show menu' : 'Hide menu'}
          onClick={() => toggle(!hidden)}
        >
          <Icon name="sidebar" size={18} />
        </button>
        {!hidden && <DocSidebar />}
      </div>
      {/* On a phone the sidebar folds into a menu at the top of the page; it closes on each new page. */}
      <details key={pathname} className="doc-mobile-nav">
        <summary>
          <Cased>Browse documentation</Cased>
          <Icon name="chevron" size={14} className="doc-mobile-nav__chevron" />
        </summary>
        <DocNav />
      </details>
      <article className={`doc-content${wide ? ' doc-content--wide' : ''}`}>
        <header className={`doc-header${aside ? ' doc-header--aside' : ''}`}>
          <div className="doc-header__text">
            {/* Inside a section, the eyebrow is the breadcrumb: the section, then this page. */}
            {section ? (
              <nav aria-label="Breadcrumb" className="mds-eyebrow doc-breadcrumb">
                <Link to={section.to}>{section.label}</Link>
                {eyebrow.startsWith(`${section.label} / `) ? eyebrow.slice(section.label.length) : ''}
                {' / '}
                <span aria-current="page">{crumb ?? title}</span>
              </nav>
            ) : (
              <span className="mds-eyebrow">{eyebrow}</span>
            )}
            <h1 className="mds-h1">
              <Cased>{title}</Cased>
            </h1>
            <div className="mds-lead doc-header__intro">{intro}</div>
          </div>
          {aside && <div className="doc-header__aside">{aside}</div>}
        </header>
        {children}
      </article>
    </div>
  )
}
